import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useGetMessagesAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useGetMessagesAction';
import { useConversationState } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { observeAnchor } from '@web/common/utils/observeAnchor';

import { ListAction, ListActionCallback, ScrollData } from './conversationIntiniteList.types';
import { getContainerScrollState, scrollToBottom } from './conversationInfiniteList.utils';

export const useScrollHandler = (containerNode: HTMLDivElement) => {
  const scrollDataRef = useRef<ScrollData | null>(null);
  const latestActionRef = useRef<ListAction>(ListAction.IDLE);
  const [listNode, setListNode] = useState<HTMLDivElement | null>(null);
  const { hasScrollLocked } = useConversationState();

  const handleScroll = useCallback(() => {
    scrollDataRef.current = getContainerScrollState(containerNode, listNode);
  }, [containerNode, listNode]);

  const saveScrollPosition = useCallback<ListActionCallback>((triggerAction) => {
    latestActionRef.current = triggerAction;
  }, []);

  useLayoutEffect(() => {
    if (!containerNode || !listNode) return;

    scrollToBottom(containerNode);
    scrollDataRef.current = getContainerScrollState(containerNode, listNode);

    const containerResizeObserver = new ResizeObserver(() => {
      const previousScrollData = scrollDataRef.current;

      if (previousScrollData.isScrolledToBottom) {
        scrollToBottom(containerNode);
      }
    });

    // INFO Safari might act strangly b/c it does not support scroll anchoring
    // more: https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor/Guide_to_scroll_anchoring
    const listNodeResizeObserver = new ResizeObserver(() => {
      const previousScrollData = scrollDataRef.current;
      const newScrollData = getContainerScrollState(containerNode, listNode);

      const { firstItem, lastItem, scrollHeight } = newScrollData;

      const hasLoadedOlder = previousScrollData.firstItem !== firstItem;
      const hasLoadedNewer = previousScrollData.lastItem !== lastItem;
      const shouldAdjustScrollPosition =
        latestActionRef.current !== ListAction.IDLE && (hasLoadedOlder || hasLoadedNewer);

      if (shouldAdjustScrollPosition) {
        if (hasLoadedOlder) {
          // let the scroll position keep the same distance from the bottom edge
          // of the scroll container
          const heightDiff = scrollHeight - previousScrollData.scrollHeight;
          containerNode.scrollTop = previousScrollData.scrollTop + heightDiff;
        }
        // no need to adjust if newer messages has beed loaded, cos appending
        // content does not change parents scrollTop - scroll remains in the same place

        latestActionRef.current = ListAction.IDLE;
      } else if (previousScrollData.isScrolledToBottom) {
        scrollToBottom(containerNode);
      }
      // otherwise let the browser handle scroll position w/ scroll anchoring algorithm

      scrollDataRef.current = newScrollData;
    });

    containerResizeObserver.observe(containerNode);
    listNodeResizeObserver.observe(listNode);

    return () => {
      containerResizeObserver.disconnect();
      listNodeResizeObserver.disconnect();
    };
  }, [containerNode, listNode]);

  return {
    handleScroll,
    hasScrollLocked,
    saveScrollPosition,
    setListNode,
  };
};

export const useInfiniteLoader = (
  containerNode: HTMLDivElement,
  onListAction: ListActionCallback
) => {
  const {
    query: {
      hasNextPage: hasOlderMessages,
      hasPreviousPage: hasNewerMessage,
      isFetchingNextPage: isLoadingOlderMessages,
      isFetchingPreviousPage: isLoadingNewerMessages,
    },
    fetchNextPage: fetchOlderMessages,
    fetchPreviousPage: fetchNewerMessages,
  } = useGetMessagesAction();

  const [loadOlderNode, setLoadOlderNode] = useState<HTMLDivElement | null>(null);
  const [loadNewerNode, setLoadNewerNode] = useState<HTMLDivElement | null>(null);

  const loadOlder = useCallback(() => {
    if (isLoadingOlderMessages) return;

    onListAction(ListAction.LOAD_OLDER);
    fetchOlderMessages();
  }, [fetchOlderMessages, isLoadingOlderMessages, onListAction]);

  const loadNewer = useCallback(() => {
    if (isLoadingNewerMessages) return;

    onListAction(ListAction.LOAD_NEWER);
    fetchNewerMessages();
  }, [fetchNewerMessages, isLoadingNewerMessages, onListAction]);

  useEffect(
    () =>
      observeAnchor(
        loadOlderNode,
        (isIntersecting) => isIntersecting && loadOlder(),
        containerNode
      ),
    [containerNode, loadOlder, loadOlderNode]
  );

  useEffect(
    () =>
      observeAnchor(
        loadNewerNode,
        (isIntersecting) => isIntersecting && loadNewer(),
        containerNode
      ),
    [containerNode, loadNewer, loadNewerNode]
  );

  return {
    loadNewer,
    loadOlder,
    hasNewerMessage,
    hasOlderMessages,
    isLoadingNewerMessages,
    isLoadingOlderMessages,
    setLoadNewerNode,
    setLoadOlderNode,
  };
};

export const useQuickAccess = (containerNode: HTMLDivElement) => {
  const { firstUnreadMessageId, unreadMessagesCount: originalUnreadMessagesCount } =
    useConversationState();

  const counterRef = useRef<number>(originalUnreadMessagesCount);
  const unreadMessagesCount = counterRef.current;
  const hasUnreadMessages = !!firstUnreadMessageId && unreadMessagesCount > 0;

  const [isScrollToNewestActive, setIsScrollToNewestActive] = useState(true);
  const [isScrollToUnreadActive, setIsScrollToUnreadActive] = useState(hasUnreadMessages);

  useEffect(() => {
    setIsScrollToUnreadActive(hasUnreadMessages);
  }, [hasUnreadMessages]);

  const [firstUnreadMessageAnchor, setFirstUnreadMessageAnchor] = useState<HTMLDivElement | null>(
    null
  );
  const [newestMessageAnchor, setNewestMessageAnchor] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isScrollToUnreadActive) return;

    return observeAnchor(
      firstUnreadMessageAnchor,
      (isIntersecting) => {
        if (isIntersecting) {
          setIsScrollToUnreadActive(false);
        }
      },
      containerNode
    );
  }, [containerNode, firstUnreadMessageAnchor, firstUnreadMessageId, isScrollToUnreadActive]);

  useEffect(
    () =>
      observeAnchor(
        newestMessageAnchor,
        (isIntersecting) => {
          const containerScrollPosition = containerNode.scrollTop;
          const newestElementPosition = newestMessageAnchor.offsetTop;
          const hasScrollPassedNewestMessage = containerScrollPosition > newestElementPosition;

          setIsScrollToNewestActive(!isIntersecting && !hasScrollPassedNewestMessage);
        },
        containerNode
      ),
    [containerNode, newestMessageAnchor]
  );

  return {
    isScrollToNewestActive,
    isScrollToUnreadActive,
    setFirstUnreadMessageAnchor,
    setNewestMessageAnchor,
    unreadMessagesCount,
  };
};
